import { StatusBadge } from "../../app/components/ui/status-badge";
import { PriceTag } from "../../app/components/ui/price-tag";
import { StatsCard } from "../../app/components/ui/stats-card";
import { ServiceCard } from "../../app/components/ui/service-card";
import { CodeBlock } from "../../app/components/ui/code-block";
import { BackgroundPattern } from "../../app/components/ui/background-pattern";
import { Activity, Cpu, Zap } from "lucide-react";

export function PlaygroundBusinessSection() {
  return (
    <div className="relative">
      <div className="border-l-8 border-black dark:border-white pl-4 mb-10">
        <p className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-1">SECTION G</p>
        <h2 className="text-3xl font-black uppercase">业务组件</h2>
      </div>

      <div className="flex flex-col gap-16 relative z-10">
        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black dark:border-white pb-2 inline-block">【G.1】StatusBadge — 状态徽章</h3>
          <p className="font-bold text-zinc-500 mb-6">用于标识不同业务状态带有标准语义颜色。</p>
          <div className="flex flex-wrap gap-6 items-center p-8 border-4 border-dashed border-black/20 dark:border-white/20 bg-white/50 dark:bg-black/50">
            <StatusBadge status="health" label="Operational" />
            <StatusBadge status="degraded" label="Degraded Performance" />
            <StatusBadge status="outage" label="Major Outage" />
            <StatusBadge status="maintenance" label="Under Maintenance" />
            <StatusBadge status="success" />
            <StatusBadge status="warning" />
            <StatusBadge status="error" />
            <StatusBadge status="info" />
            <StatusBadge status="pending" />
            <StatusBadge status="health" label="Active" showIcon={false} />
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black dark:border-white pb-2 inline-block">【G.2】PriceTag — 价格标签</h3>
          <p className="font-bold text-zinc-500 mb-6">不同字号层级的价格显示组件。</p>
          <div className="flex flex-wrap gap-8 items-end p-8 border-4 border-dashed border-black/20 dark:border-white/20 bg-white/50 dark:bg-black/50">
             <PriceTag size="sm" amount="9.99" period="month" />
             <PriceTag size="default" amount="29.00" period="month" />
             <PriceTag size="lg" amount="0.002" period="1K tokens" />
             <PriceTag size="xl" amount="99" />
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black dark:border-white pb-2 inline-block">【G.3】StatsCard — 统计卡片</h3>
          <p className="font-bold text-zinc-500 mb-6">用于控制台数据总览。</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-8 border-4 border-dashed border-black/20 dark:border-white/20 bg-white/50 dark:bg-black/50">
             <StatsCard 
               title="Total Revenue" 
               value="$45,231.89" 
               icon={Activity} 
               trend={{ value: 20.1, label: "from last month", direction: "up" }} 
               description="from last month"
             />
             <StatsCard 
               title="API Calls" 
               value="1,204,553" 
               icon={Zap} 
               trend={{ value: 4.5, label: "from last week", direction: "down" }} 
               description="from last week"
             />
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black dark:border-white pb-2 inline-block">【G.4】ServiceCard — 服务卡片</h3>
          <p className="font-bold text-zinc-500 mb-6">用于在服务市场展示各类 API 与组件服务。</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-8 border-4 border-dashed border-black/20 dark:border-white/20 bg-white/50 dark:bg-black/50">
             <ServiceCard 
               title="DeepL Pro Translation API"
               provider="DeepL GmbH"
               description="High-accuracy real-time text translation enterprise API supporting 30+ languages with glossary configurations."
               icon={Cpu}
               status="health"
               statusLabel="Operational"
               callsCount={8543200}
               tags={["Translation", "SaaS", "API"]}
               footer={<PriceTag size="sm" amount="4.99" period="1M chars" />}
             />
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black dark:border-white pb-2 inline-block">【G.5】CodeBlock — 代码块</h3>
          <p className="font-bold text-zinc-500 mb-6">用于 API 文档等展示代码，自带复制。</p>
          <div className="p-8 border-4 border-dashed border-black/20 dark:border-white/20 bg-white/50 dark:bg-black/50">
             <CodeBlock 
               language="typescript"
               showLineNumbers
               code={`import { CallAnything } from "call-anything";\n\nconst client = new CallAnything({ apiKey: "your-api-key" });\n\nconst response = await client.chat.completions.create({\n  model: "gpt-4-turbo",\n  messages: [{ role: "user", content: "Hello!" }]\n});`}
             />
          </div>
        </div>

        <div className="relative overflow-hidden">
          <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black dark:border-white pb-2 inline-block relative z-10">【G.6】BackgroundPattern — 背景图案</h3>
          <p className="font-bold text-zinc-500 mb-6 relative z-10">作为底图层使用提供视觉深度。</p>
          <div className="relative h-64 p-8 border-4 border-dashed border-black/20 dark:border-white/20">
             <BackgroundPattern pattern="dots" dotSize={2} gridSize={20} />
             <div className="relative z-10 flex items-center justify-center p-6 bg-card border shadow-lg rounded-xl max-w-sm mx-auto mt-8">
               <p className="text-center font-medium">Text floats above the pattern</p>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}
